import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import ListPage from "../../../components/ListPage";
import StatusBadge from "../../../components/StatusBadge";
import useFinanceRefunds from "../../hooks/useFinanceRefunds";
import { useApproveRefund, useIssueRefund } from "../../hooks/useRefundActions";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";
import { FinanceTabs, formatCurrency, textOrEmpty } from "../shared";
import { getUserRoles } from "../../../../utils/helpers";
import { ROLES } from "../../../../utils/constants";

// Finance Refunds tab (§4). Read-only. 11 columns — the duplicate Order ID column the
// prototype had is REMOVED (§4, resolved 2026-09-05), not rendered here. Order ID/
// Quotation ID/Partner/Partner Entity ID are already comma-joined, deduplicated and
// sorted server-side (classes/Refund/Functions.js) — this page renders them verbatim.
const PARAM = { page: "page" };

// approvalStatus is DERIVED server-side from status -- see
// pillup-api classes/Refund/Constants.js#approvalStatusFor. There is no stored column,
// so this cannot contradict the Status beside it.
const APPROVAL_TONE = { PENDING: "warning", APPROVED: "success" };

const CONFIRM_APPROVE = (id) =>
    `Approve refund ${id}?\n\nThis clears it to be sent to the payment gateway. It cannot be un-approved here.`;
const CONFIRM_ISSUE = (id) =>
    `Send refund ${id} to the payment gateway?\n\nThis moves the money and cannot be undone.`;

/**
 * The Finance console's refunds table.
 *
 * The Sales console has its OWN page (src/v2/sales/pages/refunds) rather than rendering
 * this one: the two consoles have different chrome -- ListPage plus a tab strip here, the
 * legacy card vocabulary there -- so one component could not serve both without one of
 * them looking foreign. What they DO share is the data hook and the action hooks, which
 * is everything a refund's lifecycle depends on.
 *
 * canApprove is derived from the viewer's roles rather than passed in. The Finance
 * console admits PHARMACIST alongside ADMIN and SUPER_ADMIN, but approval is gated to the
 * latter two on the server (routes/api/payment.js). Without this check a pharmacist would
 * be shown an Approve button that always 401s.
 */
// Must mirror the server's allow-list on POST /payment/approveRefund/:id and
// /payment/issueRefundById/:id (pillup-api routes/api/payment.js). SUPER_FINANCE was added
// to both 2026-09-23 by product decision; without it here the role that IS allowed to
// approve would never be shown the button.
const APPROVER_ROLES = [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SUPER_FINANCE];

function FinanceRefundsPage() {
    const canApprove = getUserRoles().some((role) => APPROVER_ROLES.includes(role));
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Math.max(Number(searchParams.get(PARAM.page)) || 1, 1);

    const approve = useApproveRefund();
    const issue = useIssueRefund();
    const busy = approve.isLoading || issue.isLoading;

    const query = useFinanceRefunds({ page, size: DEFAULT_PAGE_SIZE });
    const pagination = query.data?.pagination ?? { page, size: DEFAULT_PAGE_SIZE, total: 0 };
    const rows = query.data?.refunds ?? [];

    const columns = useMemo(
        () => [
            { key: "id", label: "Refund ID" },
            { key: "orderId", label: "Order ID", render: (row) => textOrEmpty(row.orderId) },
            { key: "name", label: "Name", render: (row) => textOrEmpty(row.name) },
            { key: "phone", label: "Phone No", render: (row) => textOrEmpty(row.phone) },
            {
                key: "amount",
                label: "Amount",
                align: "right",
                render: (row) => formatCurrency(row.amount),
            },
            { key: "status", label: "Status" },
            {
                key: "approvalStatus",
                label: "Approval Status",
                render: (row) => (
                    row.approvalStatus
                        ? <StatusBadge label={row.approvalStatus === "PENDING" ? "Pending" : "Approved"} tone={APPROVAL_TONE[row.approvalStatus] || "neutral"} />
                        : textOrEmpty(null)
                ),
            },
            { key: "transactionId", label: "Transaction ID", render: (row) => textOrEmpty(row.transactionId) },
            { key: "bankArn", label: "Bank ARN", render: (row) => textOrEmpty(row.bankArn) },
            { key: "quotationId", label: "Quotation ID", render: (row) => textOrEmpty(row.quotationId) },
            { key: "paymentId", label: "Payment ID" },
            { key: "partner", label: "Partner", render: (row) => textOrEmpty(row.partner) },
            {
                key: "partnerEntityId",
                label: "Partner Entity ID",
                render: (row) => textOrEmpty(row.partnerEntityId),
            },
            {
                key: "action",
                label: "Action",
                render: (row) => {
                    // Approve only while awaiting approval; Initiate only once approved.
                    // Rendered as absent rather than disabled, so the column never offers
                    // a control the server would refuse.
                    if (row.approvalStatus === "PENDING") {
                        if (!canApprove) return <span className="text-gray-400">Awaiting approval</span>;
                        return (
                            <button
                                type="button"
                                disabled={busy}
                                className="rounded-2xl bg-elm-500 px-3 py-1.5 font-sans text-white-50 disabled:opacity-50"
                                onClick={() => {
                                    // eslint-disable-next-line no-alert
                                    if (window.confirm(CONFIRM_APPROVE(row.id))) approve.mutate(row.id);
                                }}
                            >
                                Approve
                            </button>
                        );
                    }

                    // Approved but not yet sent. Every later status means the gateway has
                    // already been called, so there is nothing left to do from here.
                    if (row.status !== "APPROVED") return <span className="text-gray-400">—</span>;

                    return (
                        <button
                            type="button"
                            disabled={busy}
                            className="rounded-2xl bg-black px-3 py-1.5 font-sans text-white-50 disabled:opacity-50"
                            onClick={() => {
                                // eslint-disable-next-line no-alert
                                if (window.confirm(CONFIRM_ISSUE(row.id))) issue.mutate(row.id);
                            }}
                        >
                            Initiate Refund
                        </button>
                    );
                },
            },
        ],
        [canApprove, busy, approve, issue]
    );

    return (
        <>
            <FinanceTabs />
            <ListPage
                testId="finance-refunds"
                title="Refunds"
                subtitle="Refund-level view, resolved through the payment/payment-item map — not payments.orderId directly."
                columns={columns}
                rows={rows}
                query={query}
                pagination={pagination}
                onPageChange={(nextPage) => {
                    const next = new URLSearchParams(searchParams);
                    next.set(PARAM.page, String(nextPage));
                    setSearchParams(next, { replace: true });
                }}
                emptyTitle="No refunds"
                emptyHint="No refunds found for this page."
            />
        </>
    );
}

export default FinanceRefundsPage;
